import { useEffect, useMemo, useRef, useState } from 'react';
import { hashKey, useInfiniteQuery, useQueryClient, type InfiniteData, type QueryKey, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { type CellContext, type ColumnDef, type Table, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Download, RefreshCw, Search } from 'lucide-react';

import { cn } from '@/marketing/lib/utils';
import { Button } from '@/marketing/components/ui/button';
import { Input } from '@/marketing/components/ui/input';
import { useDebounce } from '@/marketing/hooks/useDebounce';
import { CONTENT_TRACKING_QUERIES } from '@/marketing/infrastructure/influencer/campaign/contentTracking/utils/queries';
import type { CampaignPipelineDefinition } from '@/marketing/models/campaignPipeline';
import {
  CONTENT_TRACKING_STAGE_FILTERS,
  type ContentTrackingCounts,
  type ContentTrackingPage,
  type ContentTrackingRow,
  type ContentTrackingStageFilter,
} from '@/marketing/models/contentTracking';
import { getStageLabel } from '@/marketing/lib/campaignPipeline';
import { getStageToneStyle } from '@/marketing/lib/pipelineTone';

import { AddContentSubmissionForm } from '../slideover/AddContentSubmissionForm';
import { ContentTrackingNotesCell } from './ContentTrackingNotesCell';
import { ContentTrackingShipmentCell } from './ContentTrackingShipmentCell';
import { ContentTrackingVideoCell } from './ContentTrackingVideoCell';
import {
  DEFAULT_CONTENT_TRACKING_FILTERS,
  toListParams,
  type ContentTrackingFilterState,
} from './contentTrackingFilters';
import { useContentTrackingExport } from './useContentTrackingExport';
import { metadataBody, useContentTrackingMutations } from './useContentTrackingMutations';
import { useRefreshContentTrackingMetrics } from './useRefreshContentTrackingMetrics';

interface ContentTrackingTabProps {
  campaignId: string;
  pipelineDefinition: CampaignPipelineDefinition;
}

const ROW_HEIGHT = 132;
const LOAD_MORE_THRESHOLD = 10;

function stageFilterLabel(stage: ContentTrackingStageFilter, pipelineDefinition: CampaignPipelineDefinition): string {
  return stage === 'Cancelled' ? 'Cancelled' : getStageLabel(pipelineDefinition, stage);
}

function StagePill({
  row,
  pipelineDefinition,
}: {
  row: ContentTrackingRow;
  pipelineDefinition: CampaignPipelineDefinition;
}) {
  if (row.stage === 'Cancelled' || row.rejected) {
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-rose-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-rose-700">
        <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-rose-600" />
        {row.rejected ? 'Rejected' : 'Cancelled'}
      </span>
    );
  }
  const tone = getStageToneStyle(pipelineDefinition, row.stage);
  return (
    <span
      className={cn(
        'tw-inline-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold',
        tone.pill,
      )}
    >
      <span className={cn('tw-h-1.5 tw-w-1.5 tw-rounded-full', tone.dot)} />
      {getStageLabel(pipelineDefinition, row.stage)}
    </span>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
  tone = 'default',
}: {
  label: string;
  count?: number | undefined;
  active: boolean;
  onClick: () => void;
  tone?: 'default' | 'rose';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'tw-inline-flex tw-h-7 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-border tw-bg-background tw-px-2.5 tw-text-[12px] tw-font-medium',
        active && 'tw-border-blue-600 tw-bg-blue-600 tw-text-white',
        !active && tone === 'rose' && 'tw-text-rose-700',
        !active && tone === 'default' && 'tw-text-foreground hover:tw-bg-muted',
      )}
    >
      {label}
      {count !== undefined && (
        <span
          className={cn(
            'tw-rounded-full tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-tabular-nums',
            active ? 'tw-bg-white/20 tw-text-white' : 'tw-bg-muted tw-text-muted-foreground',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export type { ContentTrackingCounts };

interface TrackingTableMeta {
  campaignId: string;
  queryKey: QueryKey;
  busy: boolean;
  mutations: ReturnType<typeof useContentTrackingMutations>;
  noteDrafts: Record<string, string>;
  updateNoteDraft: (key: string, notes: string | undefined) => void;
  clearSavedNoteDraft: (key: string, savedDraft: string) => void;
}

// Stable cell components preserve drafts while mutation/query state changes.
function VideosCell({ row, table }: CellContext<ContentTrackingRow, unknown>) {
  const { busy, mutations } = table.options.meta as TrackingTableMeta;
  const videos = [...row.original.videos].sort((left, right) => left.slot - right.slot);
  return (
    <div className="tw-flex tw-w-[420px] tw-flex-col tw-gap-3">
      {videos.length > 0 ? (
        <ul aria-label={`Videos for @${row.original.handle ?? row.original.influencerId}`} className="tw-m-0 tw-list-none tw-divide-y tw-divide-border tw-p-0">
          {videos.map((video) => (
            <li key={video.url} className="tw-py-3 first:tw-pt-0 last:tw-pb-0">
              <ContentTrackingVideoCell
                video={video}
                canEdit={!busy && !row.original.rejected}
                onDelete={(url) => mutations.deleteSubmission.mutate({ influencerId: row.original.influencerId, url })}
                onToggleTop={(url) => mutations.toggleTopVideo(row.original, url)}
                onSaveSparkAdsCode={(url, sparkAdsCode) => mutations.saveSparkAdsCode.mutate({
                  influencerId: row.original.influencerId,
                  body: { expectedContentVersion: row.original.contentVersion, url, ...(sparkAdsCode ? { sparkAdsCode } : {}) },
                })}
                onSaveMetrics={(url, metrics) => mutations.saveVideo.mutateAsync({
                  influencerId: row.original.influencerId,
                  body: { expectedContentVersion: row.original.contentVersion, url, ...metrics },
                })}
              />
            </li>
          ))}
        </ul>
      ) : <span className="tw-text-xs tw-text-muted-foreground">No videos yet</span>}
      <div className="tw-border-t tw-border-border tw-pt-3">
        <div className="tw-mb-2 tw-text-xs tw-font-medium">Add video</div>
        <AddContentSubmissionForm
          existingUrls={videos.filter((video) => video.hasSubmission).map((video) => video.url)}
          disabled={busy || row.original.rejected}
          onAdd={(submission) => mutations.addSubmission.mutateAsync({ influencerId: row.original.influencerId, submission })}
        />
      </div>
    </div>
  );
}

function NotesCell({ row, table }: CellContext<ContentTrackingRow, unknown>) {
  const queryClient = useQueryClient();
  const { campaignId, queryKey, busy, mutations, noteDrafts, updateNoteDraft, clearSavedNoteDraft } = table.options.meta as TrackingTableMeta;
  const draftKey = JSON.stringify([campaignId, row.original.influencerId]);
  return (
    <ContentTrackingNotesCell
      row={row.original}
      disabled={busy}
      notes={noteDrafts[draftKey] ?? row.original.metadata.notes}
      onNotesChange={(notes) => updateNoteDraft(draftKey, notes === row.original.metadata.notes ? undefined : notes)}
      onSaveNotes={async (notes) => {
        await mutations.saveMetadata.mutateAsync({
          influencerId: row.original.influencerId,
          body: metadataBody(row.original, { notes: notes.trim() }),
        });
        // Invalidation can resolve without fresh data; keep the draft until the stored note is confirmed.
        const refreshedRow = queryClient.getQueryData<InfiniteData<ContentTrackingPage>>(queryKey)?.pages
          .flatMap((page) => page.rows).find((item) => item.influencerId === row.original.influencerId);
        if (queryClient.getQueryState(queryKey)?.status === 'error' || refreshedRow?.metadata.notes !== notes.trim()) {
          throw new Error('Could not refresh saved notes');
        }
        clearSavedNoteDraft(draftKey, notes);
      }}
      onSave={(patch) => mutations.saveMetadata.mutate({
        influencerId: row.original.influencerId, body: metadataBody(row.original, patch),
      })}
    />
  );
}

export function ContentTrackingTab({ campaignId, pipelineDefinition }: ContentTrackingTabProps) {
  const [filters, setFilters] = useState<ContentTrackingFilterState>(DEFAULT_CONTENT_TRACKING_FILTERS);
  // Keep unsaved notes above virtualized/filter-keyed rows so unmounting a cell cannot discard them.
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const updateNoteDraft = (key: string, notes: string | undefined) => setNoteDrafts((current) => {
    const next = { ...current };
    if (notes === undefined) delete next[key];
    else next[key] = notes;
    return next;
  });
  const clearSavedNoteDraft = (key: string, savedDraft: string) => setNoteDrafts((current) => {
    if (current[key] !== savedDraft) return current;
    const next = { ...current };
    delete next[key];
    return next;
  });
  const debouncedSearch = useDebounce(filters.search, 300);
  const params = useMemo(() => toListParams({ ...filters, search: debouncedSearch }), [filters, debouncedSearch]);

  const queryOptions = CONTENT_TRACKING_QUERIES.list(campaignId, params);
  const query = useInfiniteQuery(queryOptions);
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.rows) ?? [], [query.data]);
  useEffect(() => {
    if (query.isFetching || query.isPlaceholderData || query.isError) return;
    setNoteDrafts((current) => {
      let next = current;
      for (const row of rows) {
        const key = JSON.stringify([campaignId, row.influencerId]);
        if (current[key] !== undefined && current[key].trim() === row.metadata.notes) {
          if (next === current) next = { ...current };
          delete next[key];
        }
      }
      return next;
    });
  }, [campaignId, rows, query.isFetching, query.isPlaceholderData, query.isError]);
  const counts = query.data?.pages[0]?.counts;
  const capabilities = query.data?.pages[0]?.capabilities;

  const mutations = useContentTrackingMutations(campaignId);
  const { exporting, exportCsv } = useContentTrackingExport(campaignId, params);
  const { progress, refresh } = useRefreshContentTrackingMetrics(campaignId);
  const busy =
    mutations.addSubmission.isPending || mutations.deleteSubmission.isPending ||
    mutations.saveVideo.isPending || mutations.saveSparkAdsCode.isPending || mutations.saveMetadata.isPending ||
    query.isFetching || query.isPlaceholderData || query.isError || progress !== null;

  const columns = useMemo<Array<ColumnDef<ContentTrackingRow>>>(() => {
    return [
      {
        id: 'creator',
        header: 'Creator',
        cell: ({ row }) => (
          <div className="tw-min-w-[150px]">
            <div className="tw-truncate tw-font-medium">
              @{row.original.handle ?? row.original.influencerId.slice(0, 8)}
            </div>
            <div className="tw-truncate tw-text-[11px] tw-capitalize tw-text-muted-foreground">
              {row.original.displayName ?? ''} {row.original.displayName ? '·' : ''} {row.original.platform ?? '—'}
            </div>
            {row.original.metadata.topVideoUrls.length > 0 && (
              <span className="tw-mt-0.5 tw-inline-block tw-rounded-full tw-bg-amber-50 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-amber-700">
                ★ Top video creator
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <div className="tw-w-[180px] tw-break-all tw-text-xs">{row.original.email || '—'}</div>
        ),
      },
      {
        id: 'stage',
        header: 'Stage',
        cell: ({ row }) => <StagePill row={row.original} pipelineDefinition={pipelineDefinition} />,
      },
      {
        id: 'shipment',
        header: 'Tracking · Package',
        cell: ({ row }) => <ContentTrackingShipmentCell row={row.original} />,
      },
      { id: 'videos', header: 'Videos', cell: VideosCell },
      {
        id: 'notes',
        header: 'Notes',
        cell: NotesCell,
      },
    ];
  }, [pipelineDefinition]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.influencerId,
    meta: { campaignId, queryKey: queryOptions.queryKey, busy, mutations, noteDrafts, updateNoteDraft, clearSavedNoteDraft } satisfies TrackingTableMeta,
  });
  const update = (patch: Partial<ContentTrackingFilterState>) => setFilters((current) => ({ ...current, ...patch }));

  return (
    <div className="tw-flex tw-flex-col">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-border tw-px-8 tw-py-3">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-muted-foreground">Stage</span>
          <FilterChip
            label="All"
            count={counts?.total}
            active={filters.stage === 'all'}
            onClick={() => update({ stage: 'all' })}
          />
          {CONTENT_TRACKING_STAGE_FILTERS.map((stage) => (
            <FilterChip
              key={stage}
              label={stageFilterLabel(stage, pipelineDefinition)}
              count={counts?.stages[stage]}
              active={filters.stage === stage}
              tone={stage === 'Cancelled' ? 'rose' : 'default'}
              onClick={() => update({ stage })}
            />
          ))}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Button
            variant="outline"
            size="sm"
            className="tw-gap-1.5"
            disabled={exporting !== null}
            onClick={() => void exportCsv('reminders')}
          >
            <Download className="tw-h-3.5 tw-w-3.5" />
            {exporting === 'reminders' ? 'Exporting…' : 'Export for reminders'}
          </Button>
          <Button
            size="sm"
            className="tw-gap-1.5"
            disabled={exporting !== null}
            onClick={() => void exportCsv('report')}
          >
            <Download className="tw-h-3.5 tw-w-3.5" />
            {exporting === 'report' ? 'Exporting…' : 'Export metrics for report'}
          </Button>
        </div>
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-border tw-px-8 tw-py-2">
        <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-muted-foreground">Content link</span>
        <FilterChip
          label="Any"
          count={counts?.total}
          active={filters.contentLink === 'any'}
          onClick={() => update({ contentLink: 'any' })}
        />
        <FilterChip
          label="Has link"
          count={counts?.hasLink}
          active={filters.contentLink === 'hasLink'}
          onClick={() => update({ contentLink: 'hasLink' })}
        />
        <FilterChip
          label="No link yet"
          count={counts?.noLink}
          active={filters.contentLink === 'noLink'}
          onClick={() => update({ contentLink: 'noLink' })}
        />
        <span className="tw-ml-3 tw-text-[11px] tw-font-semibold tw-uppercase tw-text-muted-foreground">More</span>
        <FilterChip
          label="Spark code missing"
          count={counts?.sparkMissing}
          active={filters.sparkMissing}
          onClick={() => update({ sparkMissing: !filters.sparkMissing })}
        />
        <FilterChip
          label="★ Has top video"
          count={counts?.hasTopVideo}
          active={filters.topVideoOnly}
          onClick={() => update({ topVideoOnly: !filters.topVideoOnly })}
        />
        <FilterChip
          label="TikTok"
          count={counts?.platforms['tiktok']}
          active={filters.platform === 'tiktok'}
          onClick={() => update({ platform: filters.platform === 'tiktok' ? 'all' : 'tiktok' })}
        />
        <FilterChip
          label="Instagram"
          count={counts?.platforms['instagram']}
          active={filters.platform === 'instagram'}
          onClick={() => update({ platform: filters.platform === 'instagram' ? 'all' : 'instagram' })}
        />
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-px-8 tw-py-3 tw-text-sm">
        <div className="tw-relative">
          <Search className="tw-absolute tw-left-2.5 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 tw-text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(event) => update({ search: event.target.value })}
            placeholder="Search handle, email, tracking no…"
            className="tw-h-9 tw-w-[300px] tw-pl-8"
          />
        </div>
        <span className="tw-text-muted-foreground">
          {counts ? `${counts.matching} of ${counts.total} creators` : query.isLoading ? 'Loading…' : ''}
          {rows.length > 0 && counts && rows.length < counts.matching ? ` · ${rows.length} loaded` : ''}
        </span>
        {capabilities?.metricsRefresh !== false && (
          <Button
            variant="outline"
            size="sm"
            className="tw-ml-auto tw-gap-1.5"
            disabled={busy}
            onClick={() => void refresh()}
          >
            <RefreshCw className={cn('tw-h-3.5 tw-w-3.5', progress && 'tw-animate-spin')} />
            {progress
              ? `Refreshing metrics… ${progress.refreshed}/${progress.processed}`
              : 'Refresh metrics from connected accounts'}
          </Button>
        )}
      </div>
      {query.isError && (
        <div className="tw-mx-8 tw-mb-3 tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2 tw-text-xs tw-text-red-700">
          Failed to load content tracking. {query.error instanceof Error ? query.error.message : ''}
        </div>
      )}
      {/* Reset the scroll element and virtualizer together only when the effective query changes. */}
      <ContentTrackingTable key={hashKey(queryOptions.queryKey)} table={table} query={query} />
    </div>
  );
}

interface ContentTrackingTableProps {
  table: Table<ContentTrackingRow>;
  query: UseInfiniteQueryResult<InfiniteData<ContentTrackingPage>>;
}

function ContentTrackingTable({ table, query }: ContentTrackingTableProps) {
  const tableRows = table.getRowModel().rows;

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const lastVirtualIndex = virtualRows[virtualRows.length - 1]?.index ?? -1;
  useEffect(() => {
    if (
      lastVirtualIndex >= 0 && lastVirtualIndex >= tableRows.length - LOAD_MORE_THRESHOLD &&
      query.hasNextPage && !query.isFetching && !query.isPlaceholderData && !query.isError
    ) {
      void query.fetchNextPage();
    }
  }, [lastVirtualIndex, tableRows.length, query]);

  // jsdom and a not-yet-measured scroll box report no virtual items; render every row until the virtualizer has a size.
  const isVirtualized = virtualRows.length > 0;
  const renderedRows = isVirtualized ? virtualRows.map((item) => item.index) : tableRows.map((_, index) => index);
  const paddingTop = isVirtualized ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom = isVirtualized
    ? virtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1]?.end ?? 0)
    : 0;
  return (
    <div className="tw-px-8 tw-pb-4">
      <div
        ref={scrollRef}
        className="tw-max-h-[calc(100vh-330px)] tw-overflow-auto tw-rounded-lg tw-border tw-border-border"
      >
        <table className="tw-w-full tw-min-w-max tw-text-sm">
          <thead className="tw-sticky tw-top-0 tw-z-[1] tw-bg-muted/90 tw-backdrop-blur">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="tw-text-left tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-muted-foreground"
              >
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="tw-px-3 tw-py-2.5 tw-font-semibold">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableRows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.getVisibleLeafColumns().length}
                  className="tw-px-3 tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground"
                >
                  {query.isLoading ? 'Loading creators…' : 'No creators match the current filter.'}
                </td>
              </tr>
            ) : (
              <>
                {paddingTop > 0 && (
                  <tr key="padding-top">
                    <td style={{ height: paddingTop }} colSpan={table.getVisibleLeafColumns().length} />
                  </tr>
                )}
                {renderedRows.map((rowIndex) => {
                  const row = tableRows[rowIndex];
                  if (!row) {
                    return null;
                  }
                  return (
                    <tr
                      key={row.id}
                      data-index={rowIndex}
                      ref={virtualizer.measureElement}
                      className={cn(
                        'tw-border-t tw-border-border tw-align-top',
                        row.original.metadata.cancelled && 'tw-bg-rose-50/40',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="tw-px-3 tw-py-2.5">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {paddingBottom > 0 && (
                  <tr key="padding-bottom">
                    <td style={{ height: paddingBottom }} colSpan={table.getVisibleLeafColumns().length} />
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      {query.isFetchingNextPage && (
        <div className="tw-py-2 tw-text-center tw-text-xs tw-text-muted-foreground">Loading more…</div>
      )}
      {query.isFetchNextPageError && (
        <div className="tw-py-2 tw-text-center">
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetching}
            onClick={() => { if (!query.isFetching) void query.fetchNextPage(); }}
          >
            Retry loading more
          </Button>
        </div>
      )}
    </div>
  );
}
